<script lang="ts">
  import '../../gettext'
  import config from 'config'
  import { currentUser } from '../../stores/session'
  import GalleryIcon from 'images/icon-gallery.png'
  import MapIcon from 'images/icon-map.png'
  import DatasetIcon from 'images/icon-dataset.png'
  import GroupIcon from 'images/icon-group.png'
  import DatasetDefinitionIcon from 'images/icon-dataset-definition.png'
  import WorkspaceIcon from 'images/icon-workspace.png'
  import BookmarkIcon from 'images/icon-bookmark.png'
  import DocumentIcon from 'images/icon-document.png'
  import MessageIcon from 'images/icon-message.svg?file'

  let navigation
  $: navigation = config.navigation($currentUser)
</script>

<navigation>
  <ul>
    {#each navigation.filter(menu => !!menu) as menu}
      <li tabindex="0">
        {menu.label}
        <ul class="menu" style={`${menu.width ? `min-width: ${menu.width}; max-width: ${menu.width}` : ''}`}>
          {#each menu.columns || [menu.items] as column}
            <li>
              <ul>
                {#each column.filter(item => !!item) as item}
                  {#if item.type === 'divider'}
                    <li class="divider" />
                  {:else if item.type === 'heading'}
                    <li class="heading">{@html item.label}</li>
                  {:else if item.type === 'text'}
                    <li class="text">
                      <p>
                        {@html item.text}
                        {#if item.label && item.href}
                          <a href={item.href}>{item.label}</a>
                        {/if}
                      </p>
                    </li>
                  {:else if item.type === 'link'}
                    <li>
                      <a href={item.href} class:bold={!!item.bold}>
                        {#if item.image}
                          <img src={item.image} aria-hidden="true" alt={item.label} />
                        {/if}
                        {@html item.label}
                      </a>
                    </li>
                  {:else if item.type === 'action'}
                    <li>
                      <button on:click={item.onClick} class:bold={!!item.bold}>
                        {#if item.image}
                          <img src={item.image} aria-hidden="true" alt={item.label} />
                        {/if}
                        {@html item.label}
                      </button>
                    </li>
                  {/if}
                {/each}
              </ul>
            </li>
          {/each}
        </ul>
      </li>
    {/each}

    <li tabindex="0">
      {window.gettext('Workspace')}
      <ul class="menu" style="width: 275px">
        <li>
          <ul>
            <li class="text">
              <p>
                {window.gettext(
                  'Your workspace is your dashboard for accessing and managing your content, bookmarks, and groups, as well as viewing messages and seeing your recently viewed content.',
                )}
              </p>
            </li>
            {#if $currentUser}
              <li class="divider" />
              <li>
                <a href="/workspace/">
                  <img src={WorkspaceIcon} alt={window.gettext('Workspace')} aria-hidden="true" />
                  {window.gettext('Workspace')}
                </a>
              </li>
              <li>
                <a href="/workspace/bookmarks/">
                  <img src={BookmarkIcon} alt={window.gettext('Bookmark')} aria-hidden="true" />
                  {window.gettext('Bookmarks')}
                </a>
              </li>
              {#if $currentUser.groupMembershipCount}
                <li>
                  <a href="/workspace/groups/">
                    <img src={GroupIcon} alt={window.gettext('Group')} aria-hidden="true" />
                    {window.gettext('Groups')} ({$currentUser.groupMembershipCount})
                  </a>
                </li>
              {/if}
              {#if $currentUser.galleryCount}
                <li>
                  <a href="/workspace/galleries/">
                    <img src={GalleryIcon} alt={window.gettext('Gallery')} aria-hidden="true" />
                    {window.gettext('Galleries')} ({$currentUser.galleryCount})
                  </a>
                </li>
              {/if}
              {#if $currentUser.mapCount}
                <li>
                  <a href="/workspace/maps/">
                    <img src={MapIcon} alt={window.gettext('Map')} aria-hidden="true" />
                    {window.gettext('Maps')} ({$currentUser.mapCount})
                  </a>
                </li>
              {/if}
              {#if $currentUser.datasetCount}
                <li>
                  <a href="/workspace/datasets/">
                    <img src={DatasetIcon} alt="Dataset" aria-hidden="true" />
                    {window.gettext('Datasets')} ({$currentUser.datasetCount})
                  </a>
                </li>
              {/if}
              {#if $currentUser.canCreateDefinition}
                <li>
                  <a href="/datasets/definition/">
                    <img src={DatasetDefinitionIcon} alt={window.gettext('Dataset Definition')} aria-hidden="true" />
                    {window.gettext('Dataset Definitions')} ({$currentUser.definitionCount})
                  </a>
                </li>
              {/if}
              {#if $currentUser.documentCount}
                <li>
                  <a href="/workspace/documents/">
                    <img src={DocumentIcon} alt={window.gettext('Document')} aria-hidden="true" />
                    {window.gettext('Documents')} ({$currentUser.documentCount})
                  </a>
                </li>
              {/if}
              <li class="divider" />
              <li>
                <a href="/workspace/messages/">
                  <img src={MessageIcon} alt={window.gettext('Message')} aria-hidden="true" />
                  {window.gettext('Messages')}
                </a>
              </li>
            {:else}
              <li class="text" />
              <li class="text"><p>{window.gettext('You need to be signed in to access your workspace.')}</p></li>
              <li class="divider" />
              <li class="buttons">
                <div><a href="/auth/login/" class="button">{window.gettext('Sign In')}</a></div>
                <div>or</div>
                <div><a href="/auth/create_account/">{window.gettext('Sign Up')}</a></div>
              </li>
            {/if}
          </ul>
        </li>
      </ul>
    </li>
  </ul>
</navigation>

<style lang="scss">
  navigation {
    width: $navigation-width;
    margin-top: $navigation-margin-top;
    z-index: 998;

    > ul {
      list-style: none;
      display: flex;
      align-items: center;

      > li {
        display: block;
        position: relative;
        flex-grow: 1;
        background: $navigation-background;
        border-color: $navigation-border-color;
        border-style: solid;
        border-width: $navigation-border-width;
        text-align: center;
        font-family: $navigation-font-family;
        font-size: $navigation-font-size;
        font-weight: $navigation-font-weight;
        text-shadow: $navigation-text-shadow;
        color: $navigation-color;
        padding: $navigation-padding;
        cursor: default;
        text-transform: $navigation-text-transform;

        &:hover {
          background: $navigation-hover-background;
        }

        &:first-child {
          border-radius: $navigation-first-child-border-radius;

          ul.menu {
            margin-left: 7px;
          }
        }

        &:last-child {
          border-radius: $navigation-last-child-border-radius;

          ul.menu {
            right: 7px;
          }
        }

        + li {
          margin-left: $navigation-menu-spacing;
        }

        ul.menu {
          flex-direction: row;
          position: absolute;
          display: none;
          margin-top: 8px;
          background: white;
          box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.5);
          border: 1px solid #004274;
          padding: 0;
          margin-left: -7px;
          min-width: $navigation-menu-dropdown-min-width;
          max-width: 275px;
          text-transform: none;
          z-index: 1;

          > li {
            // menu column
            flex-grow: 1;

            // This is required for IE11; otherwise text items take up the full page width.
            width: 100%;

            & + li {
              border-left: 1px solid $gray-10;
            }

            > ul {
              display: flex;
              flex-direction: column;

              > li {
                display: block;
                background: none;
                text-align: left;
                font-size: $font-size-0;
                font-weight: normal;
                text-shadow: none;
                color: $gray-0;
                padding: 0;
                margin: 0;

                &.divider {
                  height: 1px;
                  background: $gray-10;
                  margin: 5px 0;
                }

                &.heading {
                  text-align: center;
                  color: #f26522;
                  font-size: 13px;
                  font-weight: bold;
                  padding: 7px 8px;
                }

                &.text p {
                  font-size: 12px;
                  color: #555;
                  line-height: 1.2;
                  padding: 8px;
                }

                &.buttons {
                  display: flex;
                  align-items: center;
                  padding: 8px;

                  div + div {
                    margin-left: 10px;
                  }

                  div:first-child {
                    flex-grow: 1;
                    text-align: right;
                  }

                  div:last-child {
                    flex-grow: 1;
                    text-align: left;
                  }

                  .button {
                    display: inline-block;
                    text-decoration: none;
                    border-color: #fff;
                    background: linear-gradient(orange 40%, #e69500);
                    color: #fff;
                    font-weight: bold;
                    padding: 2px 15px 3px 15px;
                    font-size: $font-size-0;
                    border-radius: 15px;

                    &:hover {
                      background: linear-gradient(orange, #e69500);
                    }
                  }
                }

                a,
                button {
                  text-decoration: none;
                  color: $link-primary-color;
                }

                > a,
                > button {
                  display: block;
                  font-size: $font-size-0;
                  padding: $navigation-item-padding;

                  &.bold {
                    font-weight: bold;
                  }

                  img {
                    vertical-align: middle;
                    width: 28px;
                    height: 28px;
                  }

                  &:hover {
                    background: rgb(246, 246, 246);
                  }
                }

                > button {
                  width: 100%;
                  background: none;
                  border: none;
                  cursor: pointer;
                  text-align: left;
                  font-family: inherit;
                }
              }
            }
          }
        }

        &:hover ul.menu,
        &:focus ul.menu {
          display: flex;
        }
      }
    }
  }
</style>
